/**
 * useTierList — fetches active license tiers from the backend dynamically.
 *
 * Replaces all hardcoded TIER_ORDER arrays so newly created tiers automatically
 * appear in the privilege matrix, industry matrix, and UAMS screens.
 *
 * Returns: { tiers: string[], colors: {[name]: string}, shorts: {[name]: string}, loading: boolean }
 *
 * Call bustTierCache() after saving a new tier so the next mount reflects it.
 */

import { useState, useEffect } from 'react';
import { API, apiFetch } from './api';

// Known tiers keep their branded colours; unknown tiers cycle through EXTRA_COLORS
const KNOWN_COLORS = {
  'Lite':         '#10b981',
  'Express':      '#06b6d4',
  'Standard':     '#3b82f6',
  'Professional': '#6366f1',
  'Enterprise':   '#8b5cf6',
};
const EXTRA_COLORS = ['#ec4899', '#f59e0b', '#a855f7', '#ef4444', '#84cc16', '#14b8a6', '#0ea5e9'];

// Known tiers sort first, then alphabetically
const KNOWN_ORDER = Object.keys(KNOWN_COLORS);

function buildResult(rows) {
  const active = (Array.isArray(rows) ? rows : []).filter(
    r => r.IsActive !== false && r.IsActive !== 0
  );
  active.sort((a, b) => {
    const ai = KNOWN_ORDER.indexOf(a.LicenseTier);
    const bi = KNOWN_ORDER.indexOf(b.LicenseTier);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return (a.LicenseTier || '').localeCompare(b.LicenseTier || '');
  });

  const tiers  = active.map(r => r.LicenseTier);
  const colors = {};
  const shorts = {};
  let extraIdx = 0;
  tiers.forEach(name => {
    colors[name] = KNOWN_COLORS[name] || EXTRA_COLORS[extraIdx++ % EXTRA_COLORS.length];
    // Short label: first word or first 2 words, max 10 chars
    shorts[name] = name.split(' ').slice(0, 2).join(' ').slice(0, 10);
  });
  return { tiers, colors, shorts };
}

// ── Module-level cache ────────────────────────────────────────────────────────
let _cache     = null;
let _cacheTime = 0;
let _listeners = [];
let _fetching  = false;
const CACHE_TTL = 60_000; // 60 s — bust manually after tier CRUD

function fetchTiers() {
  if (_fetching) return;
  _fetching = true;
  apiFetch(`${API}/license/pricing`)
    .then(r => r.json())
    .then(rows => {
      _cache     = buildResult(rows);
      _cacheTime = Date.now();
      _fetching  = false;
      const fns = [..._listeners];
      _listeners = [];
      fns.forEach(fn => fn(_cache));
    })
    .catch(() => {
      _fetching  = false;
      const fns = [..._listeners];
      _listeners = [];
      fns.forEach(fn => fn(null));
    });
}

/** Call this after creating / deleting a tier so the next navigation picks it up. */
export function bustTierCache() {
  _cache     = null;
  _cacheTime = 0;
  _fetching  = false;
  _listeners = [];
}

/** React hook — returns { tiers, colors, shorts, loading } */
export function useTierList() {
  const [state, setState] = useState(() => {
    if (_cache && Date.now() - _cacheTime < CACHE_TTL) {
      return { ..._cache, loading: false };
    }
    return { tiers: [], colors: {}, shorts: {}, loading: true };
  });

  useEffect(() => {
    // Already cached and fresh — nothing to do
    if (_cache && Date.now() - _cacheTime < CACHE_TTL) {
      setState(s => s.loading ? { ..._cache, loading: false } : s);
      return;
    }

    let cancelled = false;
    _listeners.push(result => {
      if (!cancelled) {
        setState(result
          ? { ...result, loading: false }
          : { tiers: [], colors: {}, shorts: {}, loading: false }
        );
      }
    });
    fetchTiers();
    return () => { cancelled = true; };
  }, []);

  return state;
}
